import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal, computed } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { getErrorMessageCode, toTranslationKey } from '../utils/error-handling';
import { getCurrentLanguage } from '../utils/current-language';

import {
  STORAGE_KEY_EMAIL,
  COOKIE_KEY_TOKEN,
} from '../constants/storage.constants';
import { parseJwtExpiry } from '../utils/jwt-expiry';
import { InstitutionInvitationService } from './institution-invitation.service';
import { ToegangSsoService } from './toegang-sso.service';
import { publicApi } from '../http/http-context-tokens';

const SS_KEY_SSO_CHECKED = 'onegate.toegang-sso.checked';

export type LoginStep = 'email' | 'password' | 'forgot-password' | 'email-sent';

export type CheckEmailStatus =
  'ok' | 'new' | 'pending_password_setup' | 'toegang_password_setup';

export interface CheckEmailResponse {
  status: CheckEmailStatus;
}

/**
 * Login error codes the UI branches on. Extend when a new backend code drives
 * a template decision; codes that only feed `errorMessage` stay untyped.
 */
export type LoginErrorCode = 'email_not_validated' | '';

interface LoginRequest {
  email: string;
  password: string;
  invitationId?: string;
  redirectTo?: string;
}

interface LoginResponse {
  redirectUrl: string;
  state: string;
}

interface CurrentUserResponse {
  email: string;
  firstName: string | null;
  lastNamePrefix: string | null;
  lastName: string | null;
}

const readTokenFromCookie = (): string | null => {
  const cookies = document.cookie.split(';');
  const tokenCookie = cookies.find((cookie) => {
    const [name] = cookie.trim().split('=');
    return name === COOKIE_KEY_TOKEN;
  });
  if (tokenCookie) {
    const [, value] = tokenCookie.trim().split('=');
    return decodeURIComponent(value);
  }
  return null;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly #http = inject(HttpClient);

  readonly #router = inject(Router);

  readonly #invitationService = inject(InstitutionInvitationService);

  readonly #toegangSso = inject(ToegangSsoService);

  readonly email = signal<string>(
    localStorage.getItem(STORAGE_KEY_EMAIL) ?? '',
  );

  readonly firstName = signal<string | null>(null);

  readonly lastNamePrefix = signal<string | null>(null);

  readonly lastName = signal<string | null>(null);

  readonly fullName = computed(() => {
    const parts = [this.firstName(), this.lastNamePrefix(), this.lastName()]
      .map((p) => p?.trim() ?? '')
      .filter((p) => p !== '');
    return parts.length > 0 ? parts.join(' ') : '';
  });

  readonly password = signal<string>('');

  readonly step = signal<LoginStep>('email');

  readonly errorMessage = signal<string>('');

  // Raw error code from the backend, narrowed to the codes the UI actually
  // branches on. Anything else collapses to '' so the template's equality
  // check stays type-safe.
  readonly errorCode = signal<LoginErrorCode>('');

  readonly infoMessage = signal<string>('');

  readonly busy = signal(false);

  readonly token = signal<string | null>(readTokenFromCookie());

  readonly tokenExpiresAt = signal<number | null>(
    parseJwtExpiry(this.token() ?? ''),
  );

  readonly isLoggedIn = computed(() => !!this.token());

  readonly isTokenExpired = (): boolean => {
    const expiresAt = this.tokenExpiresAt();
    // No exp claim available — treat as not-yet-expired and let the backend
    // be the source of truth (the 401 interceptor still catches it).
    return expiresAt !== null && Date.now() >= expiresAt;
  };

  // Feedback state (errorMessage / errorCode / infoMessage) is mutually
  // exclusive: showing one always clears the others. Routing every mutation
  // through these helpers keeps that invariant in one place instead of
  // re-deriving it at each call site.
  readonly #setLoginError = (code: string) => {
    // errorCode is narrowed to the codes the template branches on; anything
    // else collapses to '' so the equality check stays type-safe.
    this.errorCode.set(code === 'email_not_validated' ? code : '');
    this.errorMessage.set(toTranslationKey(code, 'onegate.login.error'));
    this.infoMessage.set('');
  };

  readonly #setLoginInfo = (key: string) => {
    this.infoMessage.set(key);
    this.errorCode.set('');
    this.errorMessage.set('');
  };

  readonly #clearFeedback = () => {
    this.errorMessage.set('');
    this.errorCode.set('');
    this.infoMessage.set('');
  };

  // Clears the error state but deliberately leaves infoMessage intact, so a
  // confirmation like "validation email resent" survives the user editing the
  // password field or stepping back to it.
  readonly #clearErrors = () => {
    this.errorMessage.set('');
    this.errorCode.set('');
  };

  readonly #authRequest = (
    url: string,
    body: unknown,
    errorPrefix: string,
  ): Observable<boolean> => {
    this.busy.set(true);
    this.#clearFeedback();
    return this.#http.post(url, body, publicApi()).pipe(
      map(() => true),
      catchError((error: unknown) => {
        const code = getErrorMessageCode(error);
        this.errorMessage.set(toTranslationKey(code, errorPrefix));
        return of(false);
      }),
      finalize(() => this.busy.set(false)),
    );
  };

  readonly setEmail = (value: string) => {
    this.email.set(value);
    if (value) {
      localStorage.setItem(STORAGE_KEY_EMAIL, value);
    } else {
      localStorage.removeItem(STORAGE_KEY_EMAIL);
    }
    this.#clearFeedback();
  };

  readonly processEmail = async () => {
    this.#clearFeedback();
    if (!this.#emailValid()) {
      this.errorMessage.set('common.invalid_email');
      return;
    }

    this.busy.set(true);
    this.checkEmail(this.email()).subscribe({
      next: (response) => {
        this.busy.set(false);
        switch (response.status) {
          case 'ok':
            this.step.set('password');
            break;
          case 'pending_password_setup':
            this.#router.navigate(['/set-password']);
            break;
          case 'toegang_password_setup':
            // Conventional Toegang account: route through the set-password
            // email flow, but flag it so the screen explains the user already
            // has an account and NT2 needs a password.
            this.#router.navigate(['/set-password'], {
              queryParams: { reason: 'toegang' },
            });
            break;
          case 'new':
            this.#setLoginInfo('onegate.login.email_not_found');
            break;
          default:
            this.errorMessage.set('common.error_occurred');
            break;
        }
      },
      error: (error: unknown) => {
        this.busy.set(false);
        this.#setLoginError(getErrorMessageCode(error));
      },
    });
  };

  readonly #emailValid = () => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(this.email());
  };

  readonly checkEmail = (
    email: string,
    redirectTo?: string,
  ): Observable<CheckEmailResponse> => {
    return this.#http.post<CheckEmailResponse>(
      '/api/auth/check-email',
      {
        email,
        ...(redirectTo ? { redirectTo } : {}),
      },
      publicApi(),
    );
  };

  readonly setPassword = (value: string) => {
    this.password.set(value);
    this.#clearErrors();
  };

  /**
   * @param redirectTo Deeplink destination (e.g. an absolute Edition URL)
   * that came in on /login?redirectTo=…. The backend validates it against
   * its allowlist and threads it through the OIDC state (DUNX-81).
   */
  readonly login = (redirectTo?: string) => {
    this.busy.set(true);
    this.#clearFeedback();
    const invitationId = this.#invitationService.getId();
    const body: LoginRequest = {
      email: this.email(),
      password: this.password(),
    };
    if (invitationId) {
      body.invitationId = invitationId;
    }
    if (redirectTo) {
      body.redirectTo = redirectTo;
    }
    this.#http
      .post<LoginResponse>('/api/auth/login', body, publicApi())
      .subscribe({
        next: (response) => {
          this.#invitationService.clear();
          this.navigateExternal(response.redirectUrl);
        },
        error: (error: unknown) => {
          this.busy.set(false);
          const code = getErrorMessageCode(error);
          if (code === 'no_onegate_user') {
            // Account lives in Toegang but has no onegate password yet: send
            // them through the set-password flow (same destination as
            // check-email's 'toegang_password_setup'), flagged so the screen
            // explains they already have an account and NT2 needs a password.
            this.#router.navigate(['/set-password'], {
              queryParams: { reason: 'toegang' },
            });
            return;
          }
          this.#setLoginError(code);
        },
      });
  };

  readonly resendValidationEmail = () => {
    this.busy.set(true);
    this.#clearFeedback();
    this.#http
      .post(
        '/api/auth/resend-validation-email',
        {
          email: this.email(),
          language: getCurrentLanguage(),
        },
        publicApi(),
      )
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.#setLoginInfo('onegate.login.validation_email_resent');
        },
        error: (error: unknown) => {
          this.busy.set(false);
          this.#setLoginError(getErrorMessageCode(error));
        },
      });
  };

  readonly goToForgotPassword = () => {
    this.step.set('forgot-password');
  };

  readonly sendPasswordResetLink = () => {
    this.busy.set(true);
    this.#clearFeedback();
    this.#http
      .post(
        '/api/auth/forgot-password',
        {
          email: this.email(),
          language: getCurrentLanguage(),
        },
        publicApi(),
      )
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.step.set('email-sent');
        },
        error: (error: unknown) => {
          this.busy.set(false);
          const code = getErrorMessageCode(error);
          this.errorMessage.set(
            toTranslationKey(code, 'onegate.forgot_password.error'),
          );
        },
      });
  };

  readonly backToEmail = () => {
    this.step.set('email');
    this.password.set('');
    this.#clearFeedback();
  };

  readonly backToPassword = () => {
    this.step.set('password');
    this.#clearErrors();
  };

  readonly reset = () => {
    this.password.set('');
    this.step.set('email');
    this.#clearFeedback();
  };

  resetPassword = (password: string, token: string): Observable<boolean> =>
    this.#authRequest(
      '/api/auth/reset-password',
      { password, token },
      'onegate.reset_password.error',
    );

  verifyToken = (token: string): Observable<boolean> => {
    return this.#http
      .post<{
        email: string;
      }>('/api/auth/verify-reset-password-token', { token }, publicApi())
      .pipe(
        map((response) => {
          this.setEmail(response.email);
          return true;
        }),
        catchError((error: unknown) => {
          const code = getErrorMessageCode(
            error,
            'onegate.reset_password.invalid_token',
          );
          this.errorMessage.set(
            toTranslationKey(code, 'onegate.verify_token.error'),
          );
          return of(false);
        }),
      );
  };

  verifySetPasswordToken = (token: string): Observable<boolean> => {
    return this.#http
      .post<{
        email: string;
      }>('/api/auth/verify-set-password-token', { token }, publicApi())
      .pipe(
        map((response) => {
          this.setEmail(response.email);
          return true;
        }),
        catchError((error: unknown) => {
          const code = getErrorMessageCode(
            error,
            'onegate.set_password.invalid_token',
          );
          this.errorMessage.set(
            toTranslationKey(code, 'onegate.verify_set_password_token.error'),
          );
          return of(false);
        }),
      );
  };

  setInitialPassword = (password: string, token: string): Observable<boolean> =>
    this.#authRequest(
      '/api/auth/set-password',
      { password, token },
      'onegate.set_password.error',
    );

  initiateSetPassword = (email: string): Observable<boolean> =>
    this.#authRequest(
      '/api/auth/initiate-set-password',
      { email, language: getCurrentLanguage() },
      'onegate.initiate_set_password.error',
    );

  readonly logout = () => {
    this.#clearToken();
    this.password.set('');
    this.step.set('email');
    this.#clearFeedback();
    this.firstName.set(null);
    this.lastNamePrefix.set(null);
    this.lastName.set(null);
    this.#router.navigate(['/login']);
  };

  // eslint-disable-next-line class-methods-use-this
  protected navigateExternal(url: string): void {
    window.location.assign(url);
  }

  readonly acceptSessionToken = (token: string): void => {
    this.#setToken(token);
  };

  readonly loadCurrentUser = (): Observable<CurrentUserResponse | null> => {
    return this.#http.get<CurrentUserResponse>('/api/users/me').pipe(
      map((user) => {
        this.setEmail(user.email);
        this.firstName.set(user.firstName);
        this.lastNamePrefix.set(user.lastNamePrefix);
        this.lastName.set(user.lastName);
        return user;
      }),
      catchError(() => of(null)),
    );
  };

  /**
   * Probe for an existing Toegang SSO session. Returns true when the
   * silent-auth flow exchanged an id_token for a fresh NT2 session JWT.
   * The result is cached in sessionStorage so that, after a "no session"
   * outcome, we don't re-probe every page navigation.
   */
  readonly checkToegangSession = async (): Promise<boolean> => {
    if (this.isLoggedIn() && !this.isTokenExpired()) {
      return true;
    }
    if (sessionStorage.getItem(SS_KEY_SSO_CHECKED) === '1') {
      return false;
    }

    const result = await this.#toegangSso.checkSession();
    if (result.status === 'ok') {
      this.#setToken(result.token);
      return true;
    }
    if (result.status !== 'disabled') {
      sessionStorage.setItem(SS_KEY_SSO_CHECKED, '1');
    }
    return false;
  };

  readonly #setToken = (token: string): void => {
    this.token.set(token);
    this.tokenExpiresAt.set(parseJwtExpiry(token));
    // Deliberately no max-age: a session cookie dies with the browser, like
    // the Toegang and Edition sessions do. A persistent cookie would make the
    // bootstrap treat a returning user as logged in and forward them to
    // Edition, which no longer has a session and bounces them to Toegang's
    // login page instead of ours (DUNX-89).
    document.cookie = `${COOKIE_KEY_TOKEN}=${encodeURIComponent(token)}; path=/; SameSite=Strict`;
  };

  readonly #clearToken = (): void => {
    this.token.set(null);
    this.tokenExpiresAt.set(null);
    document.cookie = `${COOKIE_KEY_TOKEN}=; path=/; max-age=0; SameSite=Strict`;
    // Mark silent-SSO as "already checked" rather than clearing the flag,
    // so a reload after an explicit logout doesn't silently re-establish
    // the NT2 session from a still-live Toegang session. Clearing happens
    // only when a new tab/session begins (sessionStorage is per-tab).
    sessionStorage.setItem(SS_KEY_SSO_CHECKED, '1');
  };
}
